import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector as useSelectorV4 } from '@xstate/react';
import { useActorRef } from '@xstate5/react';
import { fromPromise } from 'xstate5';

import { useNoticeBannersStackActions } from '@sg/garnish';
import { type Allergen } from '@sg/graphql-schema';
import {
  dietaryPreferencesMachine,
  mergeRemoteAllergens,
  type DietaryPreferencesMachineImplementations,
  type AllergenTypes,
} from '@sg/shared-fe-features-menu';

import { DIETARY_RESTRICTIONS_STORAGE_KEY } from '../../constants';
import { type AuthMachineRefType } from '../AuthMachine';
import { useFetchDietaryPreferencesActor } from './useFetchDietaryPreferencesActor';
import { useUpdateDietaryPreferencesActor } from './useUpdateDietaryPreferencesActor';

// ─── Module-Scope Implementations (no React dependencies) ────────────────────

const onStart = () => {
  // No-op
};

const hydrateDietaryPreferencesActor = fromPromise<void, AllergenTypes>(async ({ input }) => {
  await AsyncStorage.setItem(DIETARY_RESTRICTIONS_STORAGE_KEY, JSON.stringify(input));
});

const rehydrateDietaryPreferencesActor = fromPromise<AllergenTypes | {}>(async () => {
  const stored = await AsyncStorage.getItem(DIETARY_RESTRICTIONS_STORAGE_KEY);

  if (!stored) return {};

  const parsed = JSON.parse(stored) as AllergenTypes;

  const enabledAllergens = Object.keys(parsed).filter(
    (allergen) => parsed[allergen as Allergen],
  ) as Allergen[];

  return mergeRemoteAllergens(enabledAllergens);
});

const shouldRehydrate = () => true;
const canUpdateRemotePreferences = () => true;

// ─────────────────────────────────────────────────────────────────────────────

export const useInitializeDietaryPreferencesActor = (params: Params) => {
  const { authMachineRef } = params;

  // ─── Auth State ────────────────────────────────────────────────────────────

  const isSignedIn = useSelectorV4(authMachineRef, (snapshot) =>
    snapshot.matches('session.loggedIn'),
  );

  // ───────────────────────────────────────────────────────────────────────────

  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { formatMessage } = useIntl();

  // ─── Actors ────────────────────────────────────────────────────────────────

  const fetchRemotePreferences = useFetchDietaryPreferencesActor();
  const updateRemotePreferences = useUpdateDietaryPreferencesActor();

  // ─── Machine Implementation ────────────────────────────────────────────────

  const onConfirmSuccess = useCallback(async () => {
    addNoticeBanner(
      {
        id: DIETARY_PREFERENCES_SUCCESS_BANNER_ID,
        text: formatMessage(messages.updateRestrictionsSuccess),
        palette: 'success',
      },
      true,
    );
  }, [addNoticeBanner, formatMessage]);

  const onConfirmFailure = useCallback(async () => {
    addNoticeBanner(
      {
        id: DIETARY_PREFERENCES_FAILURE_BANNER_ID,
        text: formatMessage(messages.updateRestrictionsFailure),
        palette: 'caution',
      },
      true,
    );
  }, [addNoticeBanner, formatMessage]);

  // ───────────────────────────────────────────────────────────────────────────

  const implementations: DietaryPreferencesMachineImplementations = {
    actions: {
      onStart,
      onConfirm: onConfirmSuccess,
      onExternalUpdateFailure: onConfirmFailure,
    },
    actors: {
      hydrateDietaryPreferences: hydrateDietaryPreferencesActor,
      rehydrateDietaryPreferences: rehydrateDietaryPreferencesActor,
      fetchCustomerDietaryPreferences: fetchRemotePreferences,
      updateRemotePreferences,
    },
    guards: {
      canUpdateRemotePreferences,
      shouldRehydrate,
      checkIfSignedIn: () => isSignedIn,
    },
  };

  const actorRef = useActorRef(dietaryPreferencesMachine.provide(implementations), { input: {} });

  return actorRef;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DIETARY_PREFERENCES_SUCCESS_BANNER_ID = 'dietary-preferences-success';
const DIETARY_PREFERENCES_FAILURE_BANNER_ID = 'dietary-preferences-failure';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  updateRestrictionsSuccess: {
    defaultMessage: 'Your dietary restrictions are saved!',
    description: 'Dietary Restrictions | Update | Success',
  },
  updateRestrictionsFailure: {
    defaultMessage: 'Something went wrong updating your dietary restrictions.',
    description: 'Dietary Restrictions | Update | Failure',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  authMachineRef: AuthMachineRefType;
};
